import { Box, Button, HStack, Input, Text, VStack } from "components/ui";
import { useEffect, useRef, useState } from "react";
import {
  MdCheck,
  MdClose,
  MdCreateNewFolder,
  MdDelete,
  MdEdit,
  MdFolder,
  MdFolderOpen,
  MdInbox,
  MdPublic,
} from "react-icons/md";
import { knowledgeBaseService } from "services/knowledgeBaseService";
import { KbFolderTreeDto } from "types/knowledgeBase";

export const UNCATEGORIZED_FOLDER_ID = "__uncategorized__";

interface KbFolderTreeProps {
  selectedFolderId: string | null;
  onSelect: (folderId: string | null) => void;
  onMutated?: () => void;
  onSetFolderGlobal: (id: string, isGlobal: boolean) => Promise<void>;
  onDropDocument?: (docId: string, folderId: string | null) => Promise<void>;
  onDropWebSource?: (wsId: string, folderId: string | null) => Promise<void>;
  refreshToken?: number;
}

interface FolderNodeProps {
  folder: KbFolderTreeDto;
  selectedFolderId: string | null;
  onSelect: (id: string | null) => void;
  onRename: (id: string, name: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onSetGlobal: (id: string, isGlobal: boolean) => Promise<void>;
  onDropDocument?: (docId: string, folderId: string | null) => Promise<void>;
  onDropWebSource?: (wsId: string, folderId: string | null) => Promise<void>;
}

const FolderNode = ({
  folder,
  selectedFolderId,
  onSelect,
  onRename,
  onDelete,
  onSetGlobal,
  onDropDocument,
  onDropWebSource,
}: FolderNodeProps) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(folder.name);
  const [busy, setBusy] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const renameRef = useRef<HTMLInputElement>(null);

  const isSelected = selectedFolderId === folder.id;
  const itemCount = folder.documentCount + folder.webSourceCount;

  useEffect(() => {
    if (isRenaming) renameRef.current?.focus();
  }, [isRenaming]);

  const handleRenameSubmit = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === folder.name) {
      setIsRenaming(false);
      setRenameValue(folder.name);
      return;
    }
    setBusy(true);
    try {
      await onRename(folder.id, trimmed);
    } finally {
      setBusy(false);
      setIsRenaming(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
    setIsDragOver(false);
  };
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const type = e.dataTransfer.getData("itemType");
    const id = e.dataTransfer.getData("itemId");
    if (!id) return;
    if (type === "document" && onDropDocument)
      await onDropDocument(id, folder.id);
    if (type === "webSource" && onDropWebSource)
      await onDropWebSource(id, folder.id);
  };

  return (
    <HStack
      gap={0}
      borderRadius="md"
      minH="32px"
      position="relative"
      bg={
        isDragOver
          ? { base: "blue.100", _dark: "blue.800" }
          : isSelected
            ? { base: "blue.50", _dark: "blue.900" }
            : "transparent"
      }
      borderLeft={isSelected ? "2px solid" : "2px solid transparent"}
      borderColor={isSelected ? "blue.500" : "transparent"}
      _hover={{
        bg: isSelected
          ? { base: "blue.100", _dark: "blue.800" }
          : { base: "gray.100", _dark: "gray.700" },
      }}
      transition="background 0.15s"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Folder icon */}
      <Box
        color={
          isSelected ? "blue.500" : { base: "yellow.500", _dark: "yellow.400" }
        }
        flexShrink={0}
        cursor="pointer"
        onClick={() => onSelect(folder.id)}
        px={2}
        py={1}
      >
        <MdFolder size={16} />
      </Box>

      {/* Name or rename input */}
      {isRenaming ? (
        <Input
          ref={renameRef}
          size="xs"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleRenameSubmit();
            if (e.key === "Escape") {
              setIsRenaming(false);
              setRenameValue(folder.name);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          disabled={busy}
          flex={1}
        />
      ) : (
        <Text
          fontSize="sm"
          flex={1}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          cursor="pointer"
          py={1}
          color={isSelected ? "blue.600" : undefined}
          fontWeight={isSelected ? "medium" : undefined}
          onClick={() => onSelect(folder.id)}
        >
          {folder.name}
        </Text>
      )}

      {/* Global badge — always visible when global */}
      {folder.isGlobal && !isRenaming && (
        <Box
          color="purple.500"
          flexShrink={0}
          title="Global folder — always included in all widgets"
          mr={1}
        >
          <MdPublic size={14} />
        </Box>
      )}

      {/* Item count */}
      {!isRenaming && itemCount > 0 && (
        <Text
          fontSize="xs"
          color={{ base: "gray.400", _dark: "gray.500" }}
          flexShrink={0}
          mr={1}
        >
          {itemCount}
        </Text>
      )}

      {/* Hover action buttons */}
      {!isRenaming && (
        <HStack
          gap={0}
          display={isHovered ? "flex" : "none"}
          flexShrink={0}
          pr={1}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            size="xs"
            variant="ghost"
            p={0}
            title={
              folder.isGlobal ? "Remove global flag" : "Make folder global"
            }
            color={folder.isGlobal ? "purple.500" : undefined}
            onClick={() => onSetGlobal(folder.id, !folder.isGlobal)}
          >
            <MdPublic size={14} />
          </Button>
          <Button
            size="xs"
            variant="ghost"
            title="Rename folder"
            onClick={() => {
              setIsRenaming(true);
              setRenameValue(folder.name);
            }}
            p={0}
          >
            <MdEdit size={14} />
          </Button>
          <Button
            size="xs"
            variant="ghost"
            title="Delete folder"
            colorPalette="red"
            onClick={() => onDelete(folder.id)}
            p={0}
          >
            <MdDelete size={14} />
          </Button>
        </HStack>
      )}

      {/* Confirm/cancel rename */}
      {isRenaming && (
        <HStack
          gap={0}
          flexShrink={0}
          pr={1}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            size="xs"
            variant="ghost"
            colorPalette="green"
            onClick={handleRenameSubmit}
            disabled={busy}
            p={0}
            title="Save"
          >
            <MdCheck />
          </Button>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => {
              setIsRenaming(false);
              setRenameValue(folder.name);
            }}
            p={0}
            title="Cancel"
          >
            <MdClose />
          </Button>
        </HStack>
      )}
    </HStack>
  );
};

const KbFolderTree = ({
  selectedFolderId,
  onSelect,
  onMutated,
  onSetFolderGlobal,
  onDropDocument,
  onDropWebSource,
  refreshToken,
}: KbFolderTreeProps) => {
  const [tree, setTree] = useState<KbFolderTreeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreatingRoot, setIsCreatingRoot] = useState(false);
  const [newRootName, setNewRootName] = useState("");
  const [busy, setBusy] = useState(false);
  const [isDragOverRoot, setIsDragOverRoot] = useState(false);
  const [isDragOverUncategorized, setIsDragOverUncategorized] = useState(false);
  const newRootRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isCreatingRoot) newRootRef.current?.focus();
  }, [isCreatingRoot]);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await knowledgeBaseService.getFolderTree();
      setTree(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);
  useEffect(() => {
    if (refreshToken && refreshToken > 0) refresh();
  }, [refreshToken]);

  const handleRename = async (id: string, name: string) => {
    await knowledgeBaseService.renameFolder(id, { name });
    await refresh();
    onMutated?.();
  };

  const handleDelete = async (id: string) => {
    if (
      !window.confirm("Delete this folder? Items inside will be moved to root.")
    )
      return;
    await knowledgeBaseService.deleteFolder(id);
    if (selectedFolderId === id) onSelect(null);
    await refresh();
    onMutated?.();
  };

  const handleCreateRoot = async () => {
    const trimmed = newRootName.trim();
    if (!trimmed) {
      setIsCreatingRoot(false);
      setNewRootName("");
      return;
    }
    setBusy(true);
    try {
      await knowledgeBaseService.createFolder({ name: trimmed });
      setIsCreatingRoot(false);
      setNewRootName("");
      await refresh();
      onMutated?.();
    } finally {
      setBusy(false);
    }
  };

  const handleSetGlobal = async (id: string, isGlobal: boolean) => {
    await onSetFolderGlobal(id, isGlobal);
    await refresh();
  };

  const handleRootDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOverRoot(false);
    const type = e.dataTransfer.getData("itemType");
    const id = e.dataTransfer.getData("itemId");
    if (!id) return;
    if (type === "document" && onDropDocument) await onDropDocument(id, null);
    if (type === "webSource" && onDropWebSource)
      await onDropWebSource(id, null);
  };

  return (
    <VStack align="stretch" gap={0} minW="200px">
      {/* "All items" root entry */}
      <HStack
        gap={2}
        px={2}
        py={1}
        minH="32px"
        borderRadius="md"
        cursor="pointer"
        bg={
          isDragOverRoot
            ? { base: "blue.100", _dark: "blue.800" }
            : selectedFolderId === null
              ? { base: "blue.50", _dark: "blue.900" }
              : "transparent"
        }
        borderLeft={
          selectedFolderId === null ? "2px solid" : "2px solid transparent"
        }
        borderColor={selectedFolderId === null ? "blue.500" : "transparent"}
        color={selectedFolderId === null ? "blue.600" : undefined}
        _hover={{
          bg:
            selectedFolderId === null
              ? { base: "blue.100", _dark: "blue.800" }
              : { base: "gray.100", _dark: "gray.700" },
        }}
        onClick={() => onSelect(null)}
        mb={1}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOverRoot(true);
        }}
        onDragLeave={() => setIsDragOverRoot(false)}
        onDrop={handleRootDrop}
        transition="background 0.15s"
      >
        <Box
          color={
            selectedFolderId === null
              ? "blue.500"
              : { base: "gray.500", _dark: "gray.400" }
          }
        >
          <MdFolderOpen size={16} />
        </Box>
        <Text
          fontSize="sm"
          fontWeight={selectedFolderId === null ? "medium" : undefined}
        >
          All items
        </Text>
      </HStack>

      {/* Uncategorized entry — shows only items with no folder */}
      <HStack
        gap={2}
        px={2}
        py={1}
        minH="32px"
        borderRadius="md"
        cursor="pointer"
        bg={
          isDragOverUncategorized
            ? { base: "blue.100", _dark: "blue.800" }
            : selectedFolderId === UNCATEGORIZED_FOLDER_ID
              ? { base: "blue.50", _dark: "blue.900" }
              : "transparent"
        }
        borderLeft={
          selectedFolderId === UNCATEGORIZED_FOLDER_ID
            ? "2px solid"
            : "2px solid transparent"
        }
        borderColor={
          selectedFolderId === UNCATEGORIZED_FOLDER_ID
            ? "blue.500"
            : "transparent"
        }
        color={
          selectedFolderId === UNCATEGORIZED_FOLDER_ID ? "blue.600" : undefined
        }
        _hover={{
          bg:
            selectedFolderId === UNCATEGORIZED_FOLDER_ID
              ? { base: "blue.100", _dark: "blue.800" }
              : { base: "gray.100", _dark: "gray.700" },
        }}
        onClick={() => onSelect(UNCATEGORIZED_FOLDER_ID)}
        mb={1}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOverUncategorized(true);
        }}
        onDragLeave={() => setIsDragOverUncategorized(false)}
        onDrop={handleRootDrop}
        transition="background 0.15s"
      >
        <Box
          color={
            selectedFolderId === UNCATEGORIZED_FOLDER_ID
              ? "blue.500"
              : { base: "gray.500", _dark: "gray.400" }
          }
        >
          <MdInbox size={16} />
        </Box>
        <Text
          fontSize="sm"
          fontWeight={
            selectedFolderId === UNCATEGORIZED_FOLDER_ID ? "medium" : undefined
          }
        >
          Uncategorized
        </Text>
      </HStack>

      {loading ? (
        <Text
          fontSize="xs"
          color={{ base: "gray.500", _dark: "gray.400" }}
          px={4}
          py={2}
        >
          Loading…
        </Text>
      ) : (
        tree.map((folder) => (
          <FolderNode
            key={folder.id}
            folder={folder}
            selectedFolderId={selectedFolderId}
            onSelect={onSelect}
            onRename={handleRename}
            onDelete={handleDelete}
            onSetGlobal={handleSetGlobal}
            onDropDocument={onDropDocument}
            onDropWebSource={onDropWebSource}
          />
        ))
      )}

      {/* New folder input */}
      {isCreatingRoot ? (
        <HStack gap={1} px={2} py={1} mt={1}>
          <Box
            color={{ base: "yellow.500", _dark: "yellow.400" }}
            flexShrink={0}
          >
            <MdFolder size={15} />
          </Box>
          <Input
            ref={newRootRef}
            size="xs"
            placeholder="Folder name"
            value={newRootName}
            onChange={(e) => setNewRootName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateRoot();
              if (e.key === "Escape") {
                setIsCreatingRoot(false);
                setNewRootName("");
              }
            }}
            disabled={busy}
          />
          <Button
            size="xs"
            variant="ghost"
            colorPalette="green"
            onClick={handleCreateRoot}
            disabled={busy}
            p={0}
            title="Create"
          >
            <MdCheck />
          </Button>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => {
              setIsCreatingRoot(false);
              setNewRootName("");
            }}
            p={0}
            title="Cancel"
          >
            <MdClose />
          </Button>
        </HStack>
      ) : (
        <Button
          size="xs"
          variant="ghost"
          mt={2}
          onClick={() => setIsCreatingRoot(true)}
          color={{ base: "gray.500", _dark: "gray.400" }}
          justifyContent="flex-start"
          px={2}
        >
          <MdCreateNewFolder size={14} />
          <Text fontSize="xs" ml={1}>
            New folder
          </Text>
        </Button>
      )}
    </VStack>
  );
};

export default KbFolderTree;
